import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import Hls from "hls.js";
import type { Branching, BranchingPoint } from "../api";
import { recordEvent } from "../lib/analytics";

interface AnalyticsContext {
  lo_id: string;
  web_app_id: string;
  agent_slug?: string | null;
  base_video_id?: string | null;
}

interface Props {
  basePlaybackUrl: string;
  branching: Branching | null;
  onEnded: () => void;
  poster?: string | null;
  analytics?: AnalyticsContext;
  /** Called when the base video's loadedmetadata fires so a parent can
   *  render a branching-points strip below the player. */
  onDuration?: (duration: number) => void;
}

interface PendingBranch {
  point: BranchingPoint;
  resumeAt: number;
}

interface BranchPlay {
  url: string;
  resumeAt: number;
}

const TRIGGER_WINDOW_S = 0.4;

/**
 * HLS player with branching support.
 *
 * Behavior mirrors the existing admin /playback.html:
 *  - Plays the base video. As `currentTime` reaches a branching point's
 *    timestamp (within ~0.4s), pauses and surfaces a question modal.
 *  - On option click: switches the source to the chosen branch video and
 *    plays it. When the branch ends (or the user clicks "Back to base"),
 *    we restore the base source and seek back to the original timestamp.
 *  - "Skip and Continue" dismisses the question and resumes base.
 *  - When the (possibly resumed) base video ends, we call `onEnded()`.
 */
export interface BranchingPlayerHandle {
  /** Seek the base video to the given absolute time (seconds). No-op while
   *  a branch video is playing — branches always start from the beginning. */
  seekTo: (timeSeconds: number) => void;
}

const BranchingPlayer = forwardRef<BranchingPlayerHandle, Props>(function BranchingPlayer(
  { basePlaybackUrl, branching, onEnded, poster, analytics, onDuration },
  forwardedRef,
) {
  // Helper bound to the current analytics context. No-op if not provided.
  const fire = useCallback((payload: Parameters<typeof recordEvent>[0] extends infer R ? Partial<R> : never) => {
    if (!analytics) return;
    recordEvent({
      lo_id: analytics.lo_id,
      web_app_id: analytics.web_app_id,
      agent_slug: analytics.agent_slug ?? null,
      video_id: analytics.base_video_id ?? null,
      ...payload,
    } as Parameters<typeof recordEvent>[0]);
  }, [analytics]);
  const startedRef = useRef(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const triggeredRef = useRef<Set<number>>(new Set());

  const [pending, setPending] = useState<PendingBranch | null>(null);
  const [branchPlay, setBranchPlay] = useState<BranchPlay | null>(null);

  // Imperative handle so a parent (LOPage) can seek the base video when
  // the consumer clicks a branching-points dot below the player.
  useImperativeHandle(forwardedRef, () => ({
    seekTo: (timeSeconds: number) => {
      const v = videoRef.current;
      if (!v) return;
      // Don't allow seeking inside a branch video — branches play start to
      // end as their own clip.
      if (branchPlay) return;
      const clamped = Math.max(0, Math.min(timeSeconds, v.duration || timeSeconds));
      v.currentTime = clamped;
      // If the video was paused or hadn't started, start playback so the
      // click feels responsive instead of jumping silently.
      if (v.paused) v.play().catch(() => {});
    },
  }), [branchPlay]);

  const sourceUrl = branchPlay?.url ?? basePlaybackUrl;
  const isOnBranch = !!branchPlay;

  // Attach HLS / native source whenever the source URL changes.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    if (hlsRef.current) {
      try { hlsRef.current.destroy(); } catch { /* noop */ }
      hlsRef.current = null;
    }

    const isM3U8 = sourceUrl.toLowerCase().includes(".m3u8");
    const native = v.canPlayType("application/vnd.apple.mpegurl") !== "";

    const tryPlay = () => {
      if (isOnBranch) {
        // Branch videos always autoplay; consumer chose them.
        v.play().catch(() => {});
      } else {
        // Base auto-resume only when returning from a branch.
        if (branchPlay === null && triggeredRef.current.size > 0) {
          // we were on a branch and returned; play
          v.play().catch(() => {});
        }
      }
    };

    if (isM3U8 && Hls.isSupported() && !native) {
      const hls = new Hls({ enableWorker: true });
      hls.loadSource(sourceUrl);
      hls.attachMedia(v);
      hls.on(Hls.Events.MANIFEST_PARSED, tryPlay);
      hlsRef.current = hls;
    } else {
      v.src = sourceUrl;
      const onMeta = () => {
        v.removeEventListener("loadedmetadata", onMeta);
        tryPlay();
      };
      v.addEventListener("loadedmetadata", onMeta);
    }

    return () => {
      if (hlsRef.current) {
        try { hlsRef.current.destroy(); } catch { /* noop */ }
        hlsRef.current = null;
      }
    };
  // We deliberately ignore branchPlay in deps when sourceUrl drives it.
  // sourceUrl already covers any source change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceUrl]);

  // Watch for branching triggers on the base video.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const points = branching?.points ?? [];

    const onTime = () => {
      if (isOnBranch || pending) return;
      const t = v.currentTime;
      for (const p of points) {
        if (triggeredRef.current.has(p.timestamp)) continue;
        if (Math.abs(t - p.timestamp) < TRIGGER_WINDOW_S) {
          triggeredRef.current.add(p.timestamp);
          v.pause();
          setPending({ point: p, resumeAt: p.timestamp });
          break;
        }
      }
    };

    v.addEventListener("timeupdate", onTime);
    return () => v.removeEventListener("timeupdate", onTime);
  }, [branching, isOnBranch, pending]);

  // When source changes back to base after a branch, seek to the resume point.
  useEffect(() => {
    if (branchPlay) return;
    if (!videoRef.current) return;
    // pendingResumeAt is captured in pending OR in a separate ref via setBranchPlay flow.
    // We use videoRef.dataset to stash the seek target, set by handleBack.
    const tgt = videoRef.current.dataset.resumeAt;
    if (!tgt) return;
    delete videoRef.current.dataset.resumeAt;
    const v = videoRef.current;
    const target = parseFloat(tgt);
    const onMeta = () => {
      v.removeEventListener("loadedmetadata", onMeta);
      try {
        v.currentTime = target;
        v.play().catch(() => {});
      } catch { /* noop */ }
    };
    if (v.readyState >= 1) {
      try {
        v.currentTime = target;
        v.play().catch(() => {});
      } catch { /* noop */ }
    } else {
      v.addEventListener("loadedmetadata", onMeta);
    }
  }, [branchPlay]);

  const handleEnded = useCallback(() => {
    if (isOnBranch && branchPlay) {
      // Auto-return from branch
      handleBack(branchPlay.resumeAt);
    } else {
      fire({ event_type: "video_ended" });
      onEnded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnBranch, branchPlay, onEnded, fire]);

  // Fire video_started once, the first time the base video begins playing.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => {
      if (isOnBranch || startedRef.current) return;
      startedRef.current = true;
      fire({ event_type: "video_started" });
    };
    v.addEventListener("play", onPlay);
    return () => v.removeEventListener("play", onPlay);
  }, [fire, isOnBranch]);

  function handleChoose(opt: { branchVideoId: string; playbackUrl: string | null; label?: string }) {
    if (!pending) return;
    // Capture the analytics event regardless of which kind of option it is.
    fire({
      event_type: "branch_choice",
      timestamp_in_video: pending.resumeAt,
      question: pending.point.question,
      choice_label: opt.label || null,
      branch_video_id: opt.branchVideoId || null,
    });
    // "__continue__" or any option without a branch playbackUrl means
    // "skip the branch and just keep playing the main video."
    if (opt.branchVideoId === "__continue__" || !opt.playbackUrl) {
      setPending(null);
      videoRef.current?.play().catch(() => {});
      return;
    }
    const resumeAt = pending.resumeAt;
    setPending(null);
    setBranchPlay({ url: opt.playbackUrl, resumeAt });
  }

  function handleSkip() {
    if (!pending) return;
    setPending(null);
    videoRef.current?.play().catch(() => {});
  }

  function handleBack(resumeAt?: number) {
    if (!videoRef.current) return;
    const target = resumeAt ?? branchPlay?.resumeAt ?? 0;
    videoRef.current.dataset.resumeAt = String(target);
    setBranchPlay(null);
  }

  return (
    <>
      <video
        ref={videoRef}
        className="player"
        controls
        playsInline
        preload="metadata"
        poster={poster ?? undefined}
        onEnded={handleEnded}
        onLoadedMetadata={(e) => {
          // Surface the base-video duration so a parent can render the
          // branching-points strip below the player. We only report on
          // the BASE video, not when a branch is playing, so the strip
          // stays aligned with the base timeline.
          if (!isOnBranch && onDuration) {
            const d = e.currentTarget.duration;
            if (d && isFinite(d)) onDuration(d);
          }
        }}
      />

      {pending && (
        <BranchingModal
          point={pending.point}
          onChoose={handleChoose}
          onSkip={handleSkip}
        />
      )}

      {isOnBranch && (
        <button className="branch-back-btn" onClick={() => handleBack()}>
          ← Back to main video
        </button>
      )}
    </>
  );
});

export default BranchingPlayer;

/**
 * Renders a thin timeline strip with markers indicating where branching
 * points sit in the base video. Designed to be rendered BELOW the video
 * (outside the .video-wrap that clips with overflow:hidden), between the
 * player and the action buttons. Accepts a precomputed duration so it
 * works without direct access to the videoRef.
 *
 * When `onSeek` is provided, each dot becomes a clickable button that
 * seeks the base video to that branching point's timestamp.
 */
export function BranchingDots({
  points,
  duration,
  onSeek,
}: {
  points: BranchingPoint[];
  duration: number;
  onSeek?: (timeSeconds: number) => void;
}) {
  if (!duration || !isFinite(duration)) return null;

  return (
    <div className="branch-dots">
      {points.map((p, i) => (
        <button
          key={i}
          type="button"
          className="branch-dot"
          style={{ left: `${Math.min(100, (p.timestamp / duration) * 100)}%` }}
          title={p.question}
          aria-label={`Jump to decision point: ${p.question}`}
          onClick={() => onSeek?.(p.timestamp)}
        />
      ))}
    </div>
  );
}

function BranchingModal({
  point,
  onChoose,
  onSkip,
}: {
  point: BranchingPoint;
  onChoose: (opt: { branchVideoId: string; playbackUrl: string | null; label: string }) => void;
  onSkip: () => void;
}) {
  return (
    <div className="branch-modal" role="dialog" aria-label={point.question}>
      <div className="branch-modal__card">
        <div className="branch-modal__eyebrow">Pick one to continue</div>
        <div className="branch-modal__q">{point.question}</div>
        <div className="branch-modal__opts">
          {point.options.map((o, i) => (
            <button
              key={i}
              className="branch-opt"
              onClick={() => onChoose({ ...o, label: o.label })}
            >
              <span>{o.label}</span>
            </button>
          ))}
        </div>
        <button className="branch-modal__skip" onClick={onSkip}>
          Skip and continue
        </button>
      </div>
    </div>
  );
}
